import {Injectable} from "@angular/core";
import {Product} from "@app/core/product/domain/dtos/product";
import {CheckStockUseCase} from "@app/core/stock/application/check-stock.use-case";
import {NoStockError} from "@app/core/stock/domain/errors/no-stock.error";
import {Observable, throwError} from "rxjs";
import {switchMap, tap} from "rxjs/operators";
import {CartQuantityNotifier} from "../domain/cart-quantity-notifier";
import {CartLine} from "../domain/dtos/cart-line";
import {CartLineMapper} from "../infrastructure/mappers/cart-line.mapper";
import {CartLineRepository} from "./cart-line-repository.service";
import {TrackAddToCartUseCase} from "@app/core/tracking/application/track-add-to-cart.use-case";

/**
 * Add a product to the cart.
 */
@Injectable({
    providedIn: "root",
})
export class AddProductUseCase {
    constructor(
        private cartLineRepository: CartLineRepository,
        private cartController: CartQuantityNotifier,
        private checkStockUseCase: CheckStockUseCase,
        private trackAddToCartUseCase: TrackAddToCartUseCase,
    ) { }

    execute(product: Product, quantity: number): Observable<CartLine> {
        const cartLine = CartLineMapper.fromProductToCartLine(product, quantity);

        return this.checkStockUseCase.execute([cartLine]).pipe(
            switchMap(
                checkStockResult => {
                    // If product has stock enough, add it to the cart
                    if (checkStockResult.withStock.length) {
                        this.trackAddToCart(product, quantity);
                        return this.cartLineRepository.create(cartLine).pipe(
                            // When the product is added to the cart, notify the quantity of the cart has changed.
                            tap(() => this.cartController.notifyCartQuantityChanged())
                        );
                    } else {
                        // Throw a custom error so that it can be identified by the use case caller
                        const error = new NoStockError(checkStockResult.withoutStock);
                        return throwError(() => error);
                    }
                }
            ),
        );
    }

    /**
     * Tracks the add product to cart event.
     * @param product - Product added to the cart.
     * @param quantity - Quantity of the product added to the cart.
     */
    private trackAddToCart(product: Product, quantity: number) {
        const price = product.price != null ? Number((product.price * quantity).toFixed(2)) : 0;
        this.trackAddToCartUseCase.execute({
            value: price,
            items: [{
                itemId: product.code,
                itemName: product.name,
                price: price,
                quantity: quantity
            }]
        });
    }
}
